











































































import {Component, Mixins} from 'vue-mixin-decorator';
import {Prop, Watch} from 'vue-property-decorator';
import {Inject} from 'inversify-props';

import {LoraForwarderState, LoraForwarderStateColor} from '@/model/lora/LoraForwarderState';

import {IEventsService} from '@/services/IEvents.service';
import {LoraForwarderStdService} from '@/services/lora/forwarder/LoraForwarderStd.service';

import LoraForwarderStatus from '@/components/Lora/Forwarder/LoraForwarderRunStatus.vue';
import {LoraForwarderStore} from '@/store/lora/LoraForwarder.store';
import {Notify} from '@/utils/Notify';

import {BaseComponent} from '@/mixins/BaseComponent';

@Component({
  components: {LoraForwarderStatus},
})
export default class LoraForwarderControl extends Mixins<BaseComponent>(BaseComponent) {

  private starting = false;
  private stopping = false;
  private restarting = false;

  private autoStart: boolean | null = null;
  private autoStartToggle = false;

  private LoraForwarderState = LoraForwarderState;
  private LoraForwarderStateColor = LoraForwarderStateColor;

  @Prop(Object)
  private forwarderStore!: LoraForwarderStore;

  @Prop()
  private loraForwarderGenericService!: LoraForwarderStdService;

  @Inject(Symbol.for('IEventsService'))
  private eventsService!: IEventsService;

  private async mounted() {
    await this.refreshEnabled();
    await this.refreshState();
    await this.subscribeState();
    this.setLoaded();
  }

  private async beforeDestroy() {
    await this.forwarderStore.unsubscribeState();
  }

  private async subscribeState() {
    try {
      this.logger.debug(`Subscribing to forwarder status`);
      await this.forwarderStore.subscribeState();
    } catch (error) {
      Notify.Warn(this.logger, `Could not subscribe to forwarder status`, error, ['Please refresh the status manually']);
    }
  }

  private async refreshEnabled() {
    try {
      await this.forwarderStore.fetchEnabled();
      this.autoStart = this.forwarderStore.Enabled;
    } catch (error) {
      Notify.Error(this.logger, `Could not get auto-start state`, error);
    }
  }

  private async refreshState() {
    try {
      await this.forwarderStore.fetchState();
    } catch (error) {
      this.logger.error(`Could not get state`, error);
    }
  }

  private async onAutoStartSwitchUpdate(enable: boolean) {

    if (enable) {
      await this.autoStartEnable();
    } else {
      await this.autoStartDisable();
    }
  }

  @Watch('forwarderStore.Enabled')
  private onEnabledChanged() {
    this.autoStart = this.forwarderStore.Enabled;
  }

  private async autoStartEnable() {
    this.autoStartToggle = true;
    try {
      await this.loraForwarderGenericService.enable();
      this.forwarderStore.setEnabled(true);
      Notify.Info(this.logger, `The forwarder will try to (re)start in the background`);
    } catch (error) {
      Notify.Error(this.logger, `Could not enable autostart`, error);
    } finally {
      this.autoStartToggle = false;
    }
  }

  private async autoStartDisable() {
    this.autoStartToggle = true;
    try {
      await this.loraForwarderGenericService.disable();
      this.forwarderStore.setEnabled(false);
      Notify.Info(this.logger, `Auto start is now disabled`);
    } catch (error) {
      Notify.Error(this.logger, `Could not disable autostart`, error);
    } finally {
      this.autoStartToggle = false;
    }
  }

  private async start() {
    this.starting = true;
    try {
      await this.loraForwarderGenericService.start();
      await this.autoStartEnable();
    } catch (error) {
      Notify.Error(this.logger, `Could not start the forwarder`, error, ['Check the logs for more information']);
    } finally {
      this.starting = false;
    }
  }

  private async stop() {
    await this.autoStartDisable();

    this.stopping = true;
    try {
      await this.loraForwarderGenericService.stop();
    } catch (error) {
      Notify.Error(this.logger, `Could not stop the forwarder`, error, ['Check the logs for more information']);
    } finally {
      this.stopping = false;
    }
  }

  private async restart() {
    this.restarting = true;
    try {
      await this.loraForwarderGenericService.restart();
    } catch (error) {
      Notify.Error(this.logger, `Could not restart the forwarder`, error, ['Check the logs for more information']);
    } finally {
      this.restarting = false;
    }
  }
}

